import { createSHA256 } from 'hash-wasm';

import { arrayBufferToBase64 } from './utils';

/** Same slice size as upload chunks (10 MB). */
const HASH_SLICE_SIZE = 10 * 1024 * 1024;

export interface IncrementalSha256Hasher {
  update(data: ArrayBuffer | Uint8Array): void;
  digestBase64(): string;
}

/** Incremental SHA-256 hasher; finalize once with `digestBase64` on the last chunk. */
export async function createIncrementalSha256Hasher(): Promise<IncrementalSha256Hasher> {
  const hasher = await createSHA256();
  hasher.init();

  return {
    update(data: ArrayBuffer | Uint8Array) {
      const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
      hasher.update(bytes);
    },
    digestBase64() {
      const digestBytes = hasher.digest('binary') as Uint8Array;
      return arrayBufferToBase64(digestBytes);
    },
  };
}

/**
 * Compute SHA-256 of a File in streaming slices and return standard Base64
 * (compatible with server `Convert.ToBase64String`).
 */
export async function computeFileSha256Base64(file: globalThis.File): Promise<string> {
  const hasher = await createIncrementalSha256Hasher();

  let offset = 0;
  while (offset < file.size) {
    const end = Math.min(offset + HASH_SLICE_SIZE, file.size);
    const sliceBuffer = await file.slice(offset, end).arrayBuffer();
    hasher.update(sliceBuffer);
    offset = end;
  }

  return hasher.digestBase64();
}
